/**
 * 通用批 flush 上报层 —— operationLog / requestLog / 任何"同步入队 → 异步 batch POST"
 * 场景复用。
 *
 * 设计要点（与 src/shared/telemetry/clientLog.ts 的 ring buffer 模式对齐）：
 *   - 同步入队：调用方 enqueue() 永远不阻塞业务路径。
 *   - 批大小阈值（默认 20）：达到阈值立即触发 flush。
 *   - 时间阈值（默认 1000ms）：距上次 flush 1s 触发。
 *   - 60s 窗口每分钟上限（默认 60）：超过则整批丢弃，防止后端被打爆。
 *   - 失败静默：网络/解析任何异常都吞掉；不抛、不重试、不写 console.error。
 *   - pagehide 强制 flush（beforeunload 不一定触发）。
 *
 * PII 红线：
 *   - 本文件只负责"把一批 items POST 出去"；脱敏在调用方完成。
 *   - headers / body 内容由调用方决定；这里只用 Content-Type: application/json。
 *
 * 并发约束：
 *   - flushing 标志位 + splice(0, MAX_BATCH_SIZE) 形成串行化。
 *   - 同一时刻只有一个 fetch 在跑，flush 结束后再调度下一批。
 */

export interface BatchFlusherConfig<T> {
  /** POST 目标 URL（绝对路径或 '/api/v1/...'）。 */
  readonly url: string;
  /** 把当前队列编码成请求 body；可自行做白名单 / 截断。 */
  readonly encodeBody: (items: readonly T[]) => string;
  /** 调试用名，会出现在 console.warn 前缀，便于 QA 时分辨。 */
  readonly name: string;
  /** 批大小阈值；默认 20。 */
  readonly maxBatchSize?: number;
  /** 时间阈值 ms；默认 1000。 */
  readonly flushIntervalMs?: number;
  /** 60s 窗口最大条目数；默认 60。 */
  readonly maxPerMinute?: number;
  /** fetch 实现；默认 globalThis.fetch（live 引用，避免 MSW patch 失效）。 */
  readonly fetchImpl?: typeof fetch;
  /** 超时 ms；默认 5000。 */
  readonly timeoutMs?: number;
}

export interface BatchFlusher<T> {
  /** 同步入队；队列满阈值立刻 flush。 */
  enqueue(item: T): void;
  /** 立刻 flush 当前队列（pagehide / 测试用）。返回 Promise 但调用方不必 await。 */
  flushNow(): Promise<void>;
  /** 当前队列深度（测试 / 调试用）。 */
  size(): number;
}

const DEFAULT_MAX_BATCH = 20;
const DEFAULT_INTERVAL_MS = 1000;
const DEFAULT_MAX_PER_MINUTE = 60;
const DEFAULT_TIMEOUT_MS = 5000;

export function createBatchFlusher<T>(cfg: BatchFlusherConfig<T>): BatchFlusher<T> {
  const maxBatch = cfg.maxBatchSize ?? DEFAULT_MAX_BATCH;
  const intervalMs = cfg.flushIntervalMs ?? DEFAULT_INTERVAL_MS;
  const maxPerMin = cfg.maxPerMinute ?? DEFAULT_MAX_PER_MINUTE;
  const timeoutMs = cfg.timeoutMs ?? DEFAULT_TIMEOUT_MS;

  let queue: T[] = [];
  let flushing = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let recentCount = 0;
  let recentWindowStart = Date.now();

  function takeRateLimit(): boolean {
    const now = Date.now();
    if (now - recentWindowStart >= 60_000) {
      recentWindowStart = now;
      recentCount = 0;
    }
    if (recentCount >= maxPerMin) return false;
    recentCount += 1;
    return true;
  }

  function scheduleFlush(): void {
    if (timer) return;
    timer = setTimeout(() => {
      timer = null;
      void doFlush();
    }, intervalMs);
  }

  async function doFlush(): Promise<void> {
    if (flushing) return;
    if (queue.length === 0) return;
    flushing = true;

    const batch = queue.splice(0, maxBatch);
    const body = cfg.encodeBody(batch);

    const doFetch: typeof fetch = cfg.fetchImpl
      ?? ((...args: Parameters<typeof fetch>): ReturnType<typeof fetch> => fetch(...args));

    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), timeoutMs);
    try {
      const res = await doFetch(cfg.url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        credentials: 'include',
        signal: ctrl.signal,
        keepalive: true,
      });
      // swallow：非 2xx / 204 也吞掉 —— telemetry 必须永远 silent。
      if (!res.ok && res.status !== 204) {
        /* swallow */
      }
    } catch {
      /* swallow — telemetry must never bubble */
    } finally {
      clearTimeout(t);
      flushing = false;
      if (queue.length > 0) scheduleFlush();
    }
  }

  return {
    enqueue(item: T): void {
      if (typeof window === 'undefined') return;
      try {
        if (!takeRateLimit()) return;
        queue.push(item);
        if (queue.length >= maxBatch) {
          void doFlush();
        } else {
          scheduleFlush();
        }
      } catch {
        /* swallow */
      }
    },
    flushNow(): Promise<void> {
      return doFlush();
    },
    size(): number {
      return queue.length;
    },
  };
}

/**
 * 把一组已注册的 flusher 装上 pagehide / visibilitychange 强制 flush。
 * - pagehide 触发时全部 flush（浏览器关闭 / 后退 / 切走）。
 * - visibilitychange → hidden 时也 flush（移动端切后台、tab 冻结）。
 *
 * 幂等；installOperationTests()
 * 多次调用只有第一次生效。
 */
let pagehideInstalled = false;
export function installBatchFlushOnPagehide(flushers: ReadonlyArray<BatchFlusher<unknown>>): () => void {
  if (pagehideInstalled || typeof window === 'undefined') {
    return () => undefined;
  }
  pagehideInstalled = true;

  const flushAll = (): void => {
    for (const f of flushers) {
      try {
        void f.flushNow();
      } catch {
        /* swallow */
      }
    }
  };

  const onPagehide = (): void => flushAll();
  const onVisibility = (): void => {
    if (document.visibilityState === 'hidden') flushAll();
  };

  window.addEventListener('pagehide', onPagehide);
  document.addEventListener('visibilitychange', onVisibility);

  return () => {
    window.removeEventListener('pagehide', onPagehide);
    document.removeEventListener('visibilitychange', onVisibility);
    pagehideInstalled = false;
  };
}